import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { PageHeader, Loading } from "@/components/Layout";
import { StatusBadge } from "@/components/StatusBadge";
import { Television, Stack, FilmSlate, Broadcast, TrendUp } from "@phosphor-icons/react";

export default function Dashboard() {
  const [data, setData] = useState(null);
  const navigate = useNavigate();

  useEffect(() => { api.get("/dashboard").then((r) => setData(r.data)); }, []);

  if (!data) return <><PageHeader title="Dashboard" /><Loading /></>;

  const stats = [
    { label: "Channels", value: data.channels_total, sub: `${data.channels_live} live`, icon: Television, accent: "text-[#3A86FF]" },
    { label: "Programming Blocks", value: data.blocks_total, sub: "reusable units", icon: Stack, accent: "text-violet-400" },
    { label: "Media Assets", value: data.media_total, sub: "ReVine catalog", icon: FilmSlate, accent: "text-amber-500" },
    { label: "Active Outputs", value: data.outputs_total, sub: "HLS endpoints", icon: Broadcast, accent: "text-emerald-500" },
  ];

  return (
    <>
      <PageHeader title="Dashboard" subtitle="Operations overview" />
      <div className="p-8 space-y-8 ccs-enter">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {stats.map((s) => (
            <div key={s.label} data-testid={`stat-${s.label.toLowerCase().replace(/\s/g, "-")}`}
              className="bg-[#141414] border border-[#262626] rounded-sm p-5 hover:border-[#3a3a3a] transition-colors duration-150">
              <div className="flex items-start justify-between mb-6">
                <s.icon size={22} className={s.accent} weight="regular" />
                <TrendUp size={14} className="text-[#525252]" />
              </div>
              <div className="font-head font-black text-4xl tracking-tight tabular-nums">{s.value}</div>
              <div className="text-xs text-[#A3A3A3] mt-1">{s.label}</div>
              <div className="text-[10px] font-mono text-[#525252] mt-0.5 uppercase tracking-wider">{s.sub}</div>
            </div>
          ))}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 bg-[#141414] border border-[#262626] rounded-sm">
            <div className="flex items-center justify-between px-5 py-3 border-b border-[#262626]">
              <h2 className="font-head font-semibold text-sm">Recent Channels</h2>
              <button data-testid="view-all-channels" onClick={() => navigate("/channels")}
                className="text-xs text-[#3A86FF] hover:underline">View all</button>
            </div>
            {data.recent_channels.length === 0 ? (
              <div className="p-8 text-center text-sm text-[#737373]">No channels yet.</div>
            ) : (
              <div>
                {data.recent_channels.map((c) => (
                  <div key={c.id} onClick={() => navigate(`/channels/${c.id}`)}
                    data-testid={`dash-channel-${c.id}`}
                    className="flex items-center justify-between px-5 py-3 border-b border-[#1c1c1c] last:border-0 hover:bg-[#1F1F1F] cursor-pointer transition-colors duration-150">
                    <div className="flex items-center gap-3">
                      <div className="h-8 w-8 bg-[#0A0A0A] border border-[#262626] rounded-sm flex items-center justify-center">
                        <Television size={16} className="text-[#737373]" />
                      </div>
                      <div>
                        <div className="text-sm font-medium">{c.name}</div>
                        {c.now_playing
                          ? <div className="text-[10px] font-mono text-emerald-500 truncate max-w-[220px]">NOW · {c.now_playing.event.title}</div>
                          : <div className="text-[10px] font-mono text-[#525252]">{c.resolution}</div>}
                      </div>
                    </div>
                    <StatusBadge status={c.status} />
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="bg-[#141414] border border-[#262626] rounded-sm">
            <div className="px-5 py-3 border-b border-[#262626]">
              <h2 className="font-head font-semibold text-sm">Channel Status</h2>
            </div>
            <div className="p-5 space-y-4">
              {[
                { k: "live", label: "Live", color: "bg-emerald-500" },
                { k: "draft", label: "Draft", color: "bg-violet-400" },
                { k: "off_air", label: "Off Air", color: "bg-red-500" },
              ].map((row) => {
                const v = data.status_breakdown[row.k] || 0;
                const pct = data.channels_total ? (v / data.channels_total) * 100 : 0;
                return (
                  <div key={row.k}>
                    <div className="flex justify-between text-xs mb-1.5">
                      <span className="text-[#A3A3A3]">{row.label}</span>
                      <span className="font-mono">{v}</span>
                    </div>
                    <div className="h-1.5 bg-[#0A0A0A] rounded-full overflow-hidden">
                      <div className={`h-full ${row.color} transition-all duration-500`} style={{ width: `${pct}%` }} />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
